import { CheckCircle2, Info, PhoneCall, TriangleAlert } from 'lucide-react';
import {
  AGENTIC_CALLS_FACTS,
  TERMS,
  assessAgenticReadiness,
  normalizePhoneDigits,
  type AgenticRowStatus,
} from '@pageboss/shared';
import type { GbpLocationData } from '@/lib/types';

// "Google can call your business for searchers" (operator decision 1-A,
// 2026-09-03): a zero-cost checklist read from data we already hold -- the
// profile's state, phone and hours against the site record -- plus what
// Google says it calls about and where to turn it off. No advice, no
// eligibility claim (Google publishes none), nothing posted to Google. Every
// fact is dated; a test fails when the date is older than 90 days.

const ICON: Record<AgenticRowStatus, typeof CheckCircle2> = {
  ok: CheckCircle2,
  attention: TriangleAlert,
  info: Info,
};
const ICON_CLASS: Record<AgenticRowStatus, string> = {
  ok: 'text-success-fg',
  attention: 'text-warning-fg',
  info: 'text-text-tertiary',
};

export function AgenticCallsCard({
  location,
  site,
  unverified,
  napPhoneDiverges,
}: {
  location: GbpLocationData;
  site: { phone: string | null; addressRegion: string | null } | undefined;
  /** hasVoiceOfMerchant === false: the opt-out switch needs a verified profile. */
  unverified: boolean;
  /** The NAP-divergence card above is already showing a phone mismatch. */
  napPhoneDiverges: boolean;
}) {
  const readiness = assessAgenticReadiness({
    region: location.address?.region ?? site?.addressRegion ?? null,
    serviceArea: location.serviceArea,
    gbpPhoneRaw: location.phone,
    gbpPhoneDigits: location.phone ? normalizePhoneDigits(location.phone) : null,
    sitePhoneDigits: site?.phone ? normalizePhoneDigits(site.phone) : null,
    hoursListed: location.hours !== null,
    primaryCategory: location.primaryCategory,
    napPhoneDiverges,
  });

  return (
    <section className="rounded-lg border border-border-subtle bg-card p-6" aria-labelledby="agentic-calls-heading">
      <div className="flex items-start gap-3">
        <PhoneCall className="mt-0.5 h-5 w-5 shrink-0 text-text-tertiary" aria-hidden="true" />
        <div className="min-w-0">
          <h2 id="agentic-calls-heading" className="text-lg font-semibold tracking-tight">
            Google can call your business for searchers
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            When someone searches for a service like yours, Google can phone the business on their behalf to check
            prices, availability or a booking, and show them the answer. Google calls the phone number on your profile
            unless you turn it off; turning it off needs a verified profile.
            {readiness.available === false ? ' It is not available in your state.' : ''}
          </p>
        </div>
      </div>

      <ul className="mt-4 divide-y divide-border-subtle">
        {readiness.rows.map((row) => {
          const Icon = ICON[row.status];
          return (
            <li key={row.key} className="flex items-start gap-3 py-3">
              <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${ICON_CLASS[row.status]}`} aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-text-primary">
                  {row.label}
                  <span className="sr-only">
                    {row.status === 'attention' ? ' — needs attention' : row.status === 'ok' ? ' — ok' : ''}
                  </span>
                </p>
                <p className="text-sm text-text-secondary">{row.detail}</p>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="mt-4 rounded-md bg-surface-sunken p-4 text-sm text-text-secondary">
        <p>
          <span className="font-medium text-text-primary">Your choice.</span> Leaving it on lets Google call your
          business for those searchers; turning it off means Google will not. Google does not say how that changes
          what searchers see.{' '}
          {unverified ? 'Your profile is not verified yet, so the switch is not available to you until it is. ' : ''}
          To change it, open your{' '}
          <a
            href={AGENTIC_CALLS_FACTS.optOutUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-text-primary underline underline-offset-2"
          >
            {TERMS.googleProfile}
            <span className="sr-only"> (opens in a new tab)</span>
          </a>{' '}
          and go to {AGENTIC_CALLS_FACTS.optOutPath.replace(/^Business Profile → /, '')}.
        </p>
      </div>

      <p className="mt-3 text-xs text-text-tertiary">
        Facts checked against Google’s help page on {AGENTIC_CALLS_FACTS.verifiedOn}. Nothing here is posted to Google.
      </p>
    </section>
  );
}
